import clsx from "clsx";
import { filesize } from "filesize";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import Loader from "@components/Loader";

function Attachment({
  fileName,
  fileType,
  size,
  isLoading,
  sentAt,
  onClick,
  rightAction,
  rightActions,
  className,
  maxFileNameLength = 100,
  variant = "default",
}) {
  const { t } = useTranslation();
  const fullFileName = fileType ? `${fileName}.${fileType}` : fileName;
  const hasRowAction = typeof onClick === "function";
  const actions = rightActions || (rightAction ? [rightAction] : []);
  const isCompact = variant === "compact";

  return (
    <div
      onClick={hasRowAction ? onClick : undefined}
      className={clsx(
        "flex w-full min-w-0 rounded-lg flex-row items-start",
        hasRowAction && "cursor-pointer",
        className,
      )}
      title={fullFileName}
    >
      <div
        className={clsx(
          "flex items-center justify-center rounded-xl bg-primary",
          isCompact
            ? "min-w-9 w-9 min-h-9 h-9 md:min-w-[2.5rem] md:w-10 md:min-h-[2.5rem] md:h-10"
            : "min-w-12 w-12 min-h-12 h-12 md:min-w-14 md:w-14 md:min-h-14 md:h-14",
          isLoading ? "bg-opacity-60" : "bg-opacity-70",
        )}
      >
        {isLoading ? (
          <Loader height={isCompact ? 28 : 40} width={isCompact ? 28 : 40} />
        ) : (
          <p
            className={clsx(
              "font-medium-poppins text-white",
              isCompact ? "text-xs md:text-sm" : "text-sm md:text-base",
            )}
          >
            {fileType}
          </p>
        )}
      </div>
      <div
        className={clsx(
          "flex flex-1 flex-col min-w-0",
          isCompact ? "ml-2" : "ml-3",
        )}
      >
        <p
          className={clsx(
            "font-medium-poppins text-dawn-gray mb-0.5 truncate",
            isCompact ? "text-sm md:text-base" : "text-base md:text-lg",
          )}
        >
          {fullFileName.length > maxFileNameLength
            ? `${fullFileName.slice(0, maxFileNameLength)}...`
            : fullFileName}
        </p>
        <p
          className={clsx(
            "font-light-poppins font-black text-gray-extra-light",
            isCompact
              ? "text-[11px] leading-4 md:text-xs"
              : "text-xs leading-5 md:text-sm",
          )}
        >
          {sentAt
            ? t("chat.attachmentInfo", {
                fileSize: filesize(size),
                date: format(sentAt, "dd.MM.yyyy"),
                time: format(sentAt, "HH:mm"),
              })
            : filesize(size)}
        </p>
      </div>
      {actions.length ? (
        <div className="ml-auto flex shrink-0 items-start gap-1 pl-2">
          {actions.map((action, index) => (
            <button
              key={action.title || index}
              type="button"
              className={clsx(
                "flex h-8 w-8 items-center justify-center rounded-full border-none shadow-none outline-none ring-0 transition-colors hover:bg-black hover:bg-opacity-5 focus:outline-none focus:ring-0",
                action.className,
              )}
              title={action.title}
              aria-label={action.title}
              onClick={(event) => {
                event.stopPropagation();
                action.onClick?.(event);
              }}
            >
              {action.icon}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default Attachment;
